import { useEffect } from "react"

import NumberFlow from "@number-flow/react"
import { Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import InfoTooltip from "../../../../Components/InfoTooltip/InfoTooltip"
import { RoutesPaths } from "../../../../constants/user-routes"
import { useAnimatedNumber } from "../../../../hooks/useAnimatedNumber"
import Icon from "../../../../Icon"
import {
  CARD_TO_FILTERS,
  LOW_ENRICHMENT_MAX
} from "../../../DisputesPage/pages/AllPage/constants/allPageData"
import { resetFilters, setFilters } from "../../../DisputesPage/redux/disputes.slice"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData } from "../../redux/analytics.slice"

import styles from "./queueCards.module.less"

const { Text } = Typography

// The three queues a merchant acts on, in the minimal strip the page has always used
// below the chart (no cards): a filled icon, the label, the count, split by vertical
// dividers. `tone` maps to the colour class in styles.less; the registry icons are all
// currentColor, so they inherit it.
//
// Which three: the same buckets the Disputes list leads with, and the same counting
// rules and drill-down filters (AllPage/constants/allPageData.js), so the two pages can
// never disagree.
const ITEMS = [
  {
    id: "awaiting_review",
    label: "Needs your review",
    icon: "user_circle",
    tone: "purple",
    filters: { status: ["awaiting_review"] }
  },
  {
    id: "low_enrichment",
    label: "Low enrichment",
    icon: "alert_triangle_error",
    tone: "orange",
    tooltipKey:
      "Open and Awaiting review disputes with an enrichment score of {{pct}}% or less. Thin evidence weakens the response, so these are worth filling in first.",
    filters: CARD_TO_FILTERS.low_enrichment
  },
  {
    id: "scheduled",
    label: "Scheduled",
    icon: "clock_filled",
    tone: "blue",
    tooltipKey: "Disputes with evidence queued to be submitted automatically.",
    filters: CARD_TO_FILTERS.scheduled
  }
]

const QueueItem = ({ label, value, icon, tone, tooltip, onClick }) => {
  const { t } = useTranslation()
  const animated = useAnimatedNumber(value)
  return (
    <div
      className={styles.item}
      role="button"
      tabIndex={0}
      aria-label={t(label)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <Flex align="center" gap={8}>
        <Icon type={icon} className={cls(styles.icon, styles[tone])} />
        <Text type="secondary" strong style={{ fontSize: 14 }}>
          {t(label)}
        </Text>
        {tooltip ? <InfoTooltip title={tooltip} /> : null}
      </Flex>
      <Text style={{ fontSize: 24, lineHeight: 1.4 }}>
        {value == null ? (
          "-"
        ) : (
          <NumberFlow value={animated} format={{ maximumFractionDigits: 0 }} />
        )}
      </Text>
    </div>
  )
}

// Clicking a queue deep-links into /recover/all pre-filtered to it: resetFilters clears
// any stale filter first, so the list can never be narrower than the count promises,
// then this bucket's own patch is applied.
//
// Counts come from the same statistics the Disputes list reads, within the case type but not
// the page calendar: these are "what needs attention right now" queues, and a date window
// would silently hide work. `low_enrichment` is not sent yet (no enrichment score), so that
// queue reads "-" until it is.
const QueueCards = ({ getData }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const counts = useSelector(selectSectionData(ANALYTICS_SECTION.QUEUE))?.statistics ?? {}

  useEffect(() => {
    getData(ANALYTICS_SECTION.QUEUE)
  }, [getData])

  const openFiltered = (item) => {
    dispatch(resetFilters())
    dispatch(setFilters(item.filters))
    navigate(RoutesPaths.recover.all)
  }

  return (
    <div className={styles.bar}>
      {ITEMS.map((it) => (
        <QueueItem
          key={it.id}
          {...it}
          value={counts[it.id]}
          tooltip={it.tooltipKey ? t(it.tooltipKey, { pct: LOW_ENRICHMENT_MAX }) : null}
          onClick={() => openFiltered(it)}
        />
      ))}
    </div>
  )
}

export default QueueCards
